// src/components/InviteNotification.jsx

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, X, Check, XCircle, Users } from 'lucide-react';
import collaborationService from '../services/collaborationService';
import { getAuth } from 'firebase/auth';

export default function InviteNotification() {
  const navigate = useNavigate();
  const auth = getAuth();
  const [pendingInvites, setPendingInvites] = useState([]);
  const [pendingRequests, setPendingRequests] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  // Fetch pending invites (received) and pending requests (as project owner, awaiting my
  // approval) for the current user - two independent lists, merged for display.
  useEffect(() => {
    if (!auth.currentUser) return;

    const fetchPending = async () => {
      try {
        setLoading(true);
        const [invites, requests] = await Promise.all([
          collaborationService.getPendingInvitesForUser(),
          collaborationService.getPendingRequestsForOwner(),
        ]);
        setPendingInvites(invites);
        setPendingRequests(requests);
      } catch (error) {
        console.error('Error fetching pending invites:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchPending();

    // Set up real-time listeners for both
    const unsubscribeInvites = collaborationService.listenToUserInvites((invites) => {
      setPendingInvites(invites);
    });
    const unsubscribeRequests = collaborationService.listenToOwnerRequests((requests) => {
      setPendingRequests(requests);
    });

    return () => {
      if (unsubscribeInvites) unsubscribeInvites();
      if (unsubscribeRequests) unsubscribeRequests();
    };
  }, [auth.currentUser]);

  const allPending = [...pendingInvites, ...pendingRequests];

  const handleAcceptInvite = async (projectId, inviteId) => {
    try {
      const result = await collaborationService.acceptInvite(projectId, inviteId);
      if (result.success) {
        // Remove from pending invites
        setPendingInvites(prev => prev.filter(invite => invite.id !== inviteId));
        
        // Dispatch custom event to trigger sidebar refresh
        window.dispatchEvent(new CustomEvent('projectInvitationAccepted', {
          detail: { projectId, inviteId }
        }));
        
        // Navigate to the project
        navigate(`/project/${projectId}`);
      } else {
        alert(result.message);
      }
    } catch (error) {
      console.error('Error accepting invite:', error);
      alert('Failed to accept invite');
    }
  };

  const handleDeclineInvite = async (projectId, inviteId) => {
    try {
      const result = await collaborationService.declineInvite(projectId, inviteId);
      if (result.success) {
        // Remove from pending invites
        setPendingInvites(prev => prev.filter(invite => invite.id !== inviteId));

        // Dispatch custom event to trigger sidebar refresh (in case pending count needs update)
        window.dispatchEvent(new CustomEvent('projectInvitationDeclined', {
          detail: { projectId, inviteId }
        }));
      } else {
        alert(result.message);
      }
    } catch (error) {
      console.error('Error declining invite:', error);
      alert('Failed to decline invite');
    }
  };

  const handleApproveRequest = async (projectId, requestId) => {
    try {
      const result = await collaborationService.approveCollaboratorRequest(projectId, requestId);
      if (result.success) {
        setPendingRequests(prev => prev.filter(request => request.id !== requestId));
        window.dispatchEvent(new CustomEvent('projectInvitationAccepted', {
          detail: { projectId, inviteId: requestId }
        }));
      } else {
        alert(result.message);
      }
    } catch (error) {
      console.error('Error approving access request:', error);
      alert('Failed to approve request');
    }
  };

  const handleDenyRequest = async (projectId, requestId) => {
    try {
      const result = await collaborationService.removeCollaborator(projectId, requestId);
      if (result.success) {
        setPendingRequests(prev => prev.filter(request => request.id !== requestId));
      } else {
        alert(result.message);
      }
    } catch (error) {
      console.error('Error denying access request:', error);
      alert('Failed to deny request');
    }
  };

  if (!auth.currentUser || allPending.length === 0) {
    return null;
  }

  return (
    <>
      {/* Notification Bell */}
      <div style={{ position: 'relative' }}>
        <button
          onClick={() => setIsOpen(!isOpen)}
          style={{
            background: 'var(--branding-app-primary)',
            color: 'var(--branding-app-primary-fg)',
            border: 'none',
            borderRadius: '50%',
            width: '40px',
            height: '40px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            position: 'relative',
            boxShadow: '0 2px 8px rgba(59, 130, 246, 0.3)'
          }}
        >
          <Bell size={20} />
          {allPending.length > 0 && (
            <span style={{
              position: 'absolute',
              top: '-4px',
              right: '-4px',
              background: '#ef4444',
              color: '#fff',
              borderRadius: '50%',
              width: '20px',
              height: '20px',
              fontSize: '12px',
              fontWeight: '600',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              {allPending.length}
            </span>
          )}
        </button>

        {/* Dropdown */}
        {isOpen && (
          <div style={{
            position: 'absolute',
            top: '100%',
            right: 0,
            background: 'var(--cc-card-bg)',
            border: '1px solid var(--branding-app-surface-panel-border)',
            borderRadius: '12px',
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
            zIndex: 1000,
            minWidth: '320px',
            maxWidth: '400px',
            marginTop: '8px'
          }}>
            {/* Header */}
            <div style={{
              padding: '16px 20px',
              borderBottom: '1px solid var(--branding-app-surface)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Users size={18} color="var(--branding-app-primary)" />
                <h3 style={{
                  margin: 0,
                  fontSize: '16px',
                  fontWeight: '600',
                  color: 'var(--cc-text-primary)'
                }}>
                  Project invites &amp; requests
                </h3>
              </div>
              <button
                onClick={() => setIsOpen(false)}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: 'var(--cc-text-muted)',
                  cursor: 'pointer',
                  padding: '4px',
                  borderRadius: '4px'
                }}
              >
                <X size={16} />
              </button>
            </div>

            {/* Invites List */}
            <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
              {loading ? (
                <div style={{
                  padding: '20px',
                  textAlign: 'center',
                  color: 'var(--cc-text-muted)'
                }}>
                  Loading invites...
                </div>
              ) : allPending.length === 0 ? (
                <div style={{
                  padding: '20px',
                  textAlign: 'center',
                  color: 'var(--cc-text-muted)'
                }}>
                  Nothing pending
                </div>
              ) : (
                allPending.map((item) => {
                  const isRequest = item.kind === 'request';
                  return (
                  <div key={`${item.kind}-${item.id}`} style={{
                    padding: '16px 20px',
                    borderBottom: '1px solid var(--branding-app-surface-panel)'
                  }}>
                    <div style={{ marginBottom: '8px' }}>
                      <h4 style={{
                        margin: '0 0 4px 0',
                        fontSize: '14px',
                        fontWeight: '600',
                        color: 'var(--cc-text-primary)'
                      }}>
                        {item.projectName}
                      </h4>
                      <p style={{
                        margin: 0,
                        fontSize: '12px',
                        color: 'var(--cc-text-muted)'
                      }}>
                        {isRequest ? `${item.requesterName} requested access` : `Invited by ${item.invitedByName}`}
                      </p>
                      <p style={{
                        margin: '4px 0 0 0',
                        fontSize: '12px',
                        color: 'var(--cc-text-muted)'
                      }}>
                        Role: {item.role === 'pm_collaborator' ? 'Collaborator' : 'Viewer'}
                      </p>
                    </div>

                    <div style={{
                      display: 'flex',
                      gap: '8px',
                      marginTop: '12px'
                    }}>
                      <button
                        onClick={() => isRequest
                          ? handleApproveRequest(item.projectId, item.id)
                          : handleAcceptInvite(item.projectId, item.id)}
                        style={{
                          flex: 1,
                          background: '#10b981',
                          color: '#fff',
                          border: 'none',
                          borderRadius: '6px',
                          padding: '8px 12px',
                          fontSize: '12px',
                          fontWeight: '500',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '4px'
                        }}
                      >
                        <Check size={14} />
                        {isRequest ? 'Approve' : 'Accept'}
                      </button>
                      <button
                        onClick={() => isRequest
                          ? handleDenyRequest(item.projectId, item.id)
                          : handleDeclineInvite(item.projectId, item.id)}
                        style={{
                          flex: 1,
                          background: '#ef4444',
                          color: '#fff',
                          border: 'none',
                          borderRadius: '6px',
                          padding: '8px 12px',
                          fontSize: '12px',
                          fontWeight: '500',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '4px'
                        }}
                      >
                        <XCircle size={14} />
                        {isRequest ? 'Deny' : 'Decline'}
                      </button>
                    </div>
                  </div>
                  );
                })
              )}
            </div>
          </div>
        )}
      </div>
    </>
  );
}
